import { useEffect, useMemo, useState } from "react";
import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { SelectInput } from "~/components/SelectInput";
import { useApiQuery } from "~/modules/filters/components/useApiQuery";
import {
    AggregateFunctions,
    AggregateQueryMode,
    API_QUERY_BASE_URLS,
    ApiQuery,
    ApiQueryEndpoints,
    QueryResponse,
} from "~/modules/filters/types";
import {
    AggregateItem,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { TokenAggregatesBarChart } from "~/modules/visualization/components/TokenAggregatesBarChart";
import { BarChartType, BarChartConfig } from "~/modules/charts/types";
import { buildTimeseriesQuery } from "../ChartForm/timeseries/timeseriesConfig";
import { getChartHeight } from "~/modules/flows_explorer/pages/list/utils";
import { DEFAULT_BAR_CHART_GROUP_LIMIT } from "../ChartForm/consts";
import { TOKEN_TIMESERIES_GROUP_BY_LABELS } from "~/modules/token_timeseries/consts";
import { VisualizationApiToolsButton } from "~/modules/visualization/components/VisualizationApiToolsButton";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { ExportTableNames } from "~/modules/downloads/types";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";

// // // //

/**
 * BarChartProvider
 * Fetches the token aggregates behind a bar chart and exposes the chart,
 * loading/empty states, and an optional group-by control to its render prop.
 * Mirrors `CompositionChartProvider` - same query, different renderer.
 */
export function BarChartProvider(props: {
    title: string;
    description: string;
    config: BarChartConfig;
    /**
     * Height of a column chart. Horizontal bar charts ignore it and size to
     * their row count instead, like the net flows chart.
     */
    height: number;
    children: (props: {
        title: string;
        description: string;
        isLoading: boolean;
        isEmpty: boolean;
        chartEl: React.ReactNode;
        emptyEl: React.ReactNode;
        loadingEl: React.ReactNode;
        customControls: React.ReactNode;
    }) => React.ReactNode;
}) {
    const { title, description, config } = props;
    const isColumn = config.chart_type === BarChartType.column;

    const groupByOptions =
        (config.groups_by_options as TokenTimeseriesGroupBy[]) ?? [];

    // Re-join the separately-stored measure_slug with the rest of the query.
    const query = useMemo(() => buildTimeseriesQuery(config), [config]);

    // Default to the viewer's first group-by option, falling back to the
    // groupBy configured on the query in the Data tab, then to asset class.
    const defaultGroupBy =
        config.default_group_by ??
        groupByOptions[0] ??
        (query.aggregate?.groupBy as TokenTimeseriesGroupBy | undefined) ??
        TokenTimeseriesGroupBy.assetClass;

    // Set the group-by to the default value.
    const [groupBy, setGroupBy] =
        useState<TokenTimeseriesGroupBy>(defaultGroupBy);

    // Reset the group-by to the default value when the default value changes.
    useEffect(() => {
        setGroupBy(defaultGroupBy);
    }, [defaultGroupBy]);

    // Build the materialzed ApiQuery based on the query and the groupBy and groupLimit
    const materializedApiQuery: ApiQuery = {
        ...query,
        aggregate: {
            ...query.aggregate,
            groupBy: groupBy,
            groupLimit: 100, // NOTE - we may want to make this configurable
            // ENHANCEMENT - allow users to specifiy alternative Aggregate Function choice
            aggregateFunction: AggregateFunctions.Sum,
            // ENHANCEMENT - allow users to specifiy alternative Aggregate Mode choice
            mode: AggregateQueryMode.stock,
        },
    };

    // Fetch the data.
    const queryReq = useApiQuery({
        endpoint: ApiQueryEndpoints.TokenAggregatesV4,
        query: materializedApiQuery,
    });

    // Check if the data is loading.
    const loading = queryReq.isLoading || false;

    // Get the data.
    const data = queryReq.data as QueryResponse<AggregateItem>;

    const groupLimit =
        (config.group_limit as number) ?? DEFAULT_BAR_CHART_GROUP_LIMIT;

    // Sort by value descending, then keep only the top `group_limit` groups so
    // the chart renders the most significant bars rather than capping the API.
    const limitedResults = useMemo(() => {
        return [...(data?.results ?? [])]
            .sort((a, b) => b.value - a.value)
            .slice(0, groupLimit);
    }, [data?.results, groupLimit]);

    // Check if the data is empty.
    const isEmpty = limitedResults.length === 0;

    // Horizontal bars stack one row per group, so the chart grows with the
    // number of groups - roomy rows for a handful, tighter rows for many.
    // While loading, size for the configured limit so the card doesn't jump.
    const height = isColumn
        ? props.height
        : getChartHeight(loading ? groupLimit : limitedResults.length);

    // Define custom controls if allowedGroupByValues are present
    const customControls = (
        <div className="w-full flex flex-wrap items-center gap-3 justify-start @3xl:justify-end">
            {groupByOptions.length > 1 && (
                // The label and its select stay on the same line, and the
                // select's `w-full` resolves against this content-sized wrapper
                // instead of claiming the whole control row.
                <div className="flex items-center gap-3">
                    <p className="text-sm text-foreground whitespace-nowrap select-none">
                        Group By
                    </p>
                    <SelectInput
                        name="asset-bar-chart--select"
                        value={groupBy}
                        size="sm"
                        disabled={queryReq.isLoading}
                        onChange={(updatedValue) => {
                            setGroupBy(updatedValue as TokenTimeseriesGroupBy);
                        }}
                        options={groupByOptions.map((option) => ({
                            value: option,
                            content: {
                                label: TOKEN_TIMESERIES_GROUP_BY_LABELS[option],
                            },
                        }))}
                    />
                </div>
            )}

            <VisualizationApiToolsButton
                query={materializedApiQuery}
                apiDocsBaseUrl={
                    API_QUERY_BASE_URLS[ApiQueryEndpoints.TokenAggregatesV4]
                }
                size="xs"
            />

            <DataExportProvider>
                {({ isLoading, exportCsv }) => (
                    <DownloadButton
                        disabled={isLoading}
                        onClick={() => {
                            exportCsv({
                                exportTableName:
                                    ExportTableNames.token_aggregates_v4,
                                query: materializedApiQuery,
                            });
                        }}
                    />
                )}
            </DataExportProvider>
        </div>
    );

    // Build the description display.
    const descriptionDisplay =
        groupByOptions.length === 1
            ? `Grouped by ${TOKEN_TIMESERIES_GROUP_BY_LABELS[groupBy]}`
            : description;

    const loadingEl = (
        <div
            className={`flex items-center justify-center`}
            style={{
                height: `${height}px`,
            }}
        >
            <LogoLoadingSpinner size={75} />
        </div>
    );
    const emptyEl = (
        <div
            className={`flex items-center justify-center text-sm text-muted-foreground`}
            style={{
                height: `${height}px`,
            }}
        >
            No data matches the current filters.
        </div>
    );

    const chartEl = (
        <TokenAggregatesBarChart
            height={height}
            data={limitedResults}
            orientation={isColumn ? "column" : "bar"}
        />
    );

    return (
        <>
            {props.children({
                title,
                description: descriptionDisplay,
                isLoading: loading,
                isEmpty,
                chartEl,
                emptyEl,
                loadingEl,
                customControls,
            })}
        </>
    );
}
